import React from 'react';
import { GeoJsonLayer, TextLayer } from '@deck.gl/layers';
import { DeckGL } from '@deck.gl/react';
import { type Layer } from '@deck.gl/core';
import { Loader2 } from 'lucide-react';
import { useDmaBoundaries } from './useDmaBoundaries';
import { useWebglContextRecovery } from './useWebglContextRecovery';
import { featureLabelPoint } from './featureLabelPoint';
import { computeFitViewState, dmaKeyOf } from './mapFitViewState';
import { dmaNameForCode } from '../targetingRow';

interface InteractiveDmasMapProps {
  // Selected DMA ids (identical to the 3-digit Nielsen codes in DMA_REGIONS).
  selectedDMAs: string[];
  onChange: (next: string[]) => void;
  accentColor: string;
  // 3-digit codes of the publisher's coverage DMAs. Coverage + selected
  // markets get persistent labels; when unset the map opens on the
  // national view.
  focusDmaCodes?: string[];
  // Codes the user may toggle (coverage + markets within 50 miles, computed
  // server-side). Disallowed markets render dimmed and clicks/hover are
  // no-ops on them, mirroring InteractiveStatesMap. When null/undefined,
  // all markets are selectable and the view fits the coverage alone.
  allowedDmaCodes?: string[] | null;
}

const MAP_HEIGHT = 420;

// Zoom cap for a small coverage area (single-DMA stations hit this): keeps
// a ring of neighboring markets visible around the coverage market so
// there's something nearby to click at typical panel widths (~700px).
const DMA_MAX_ZOOM = 6;

// Interactive DMA picker (CTV-674), the DMA analogue of InteractiveStatesMap.
// Renders the census_dmas boundaries (same payload the report heatmap uses),
// fits the publisher's coverage markets at the measured container size, and
// toggles selection on click. Hovering shows the market name in a tooltip;
// persistent labels are drawn only for coverage + selected markets (210
// labels would be unreadable).
const InteractiveDmasMap: React.FC<InteractiveDmasMapProps> = ({
  selectedDMAs,
  onChange,
  accentColor,
  focusDmaCodes,
  allowedDmaCodes,
}) => {
  const { data: dmaGeoJson, status: boundariesStatus, retry: retryBoundaries } = useDmaBoundaries();
  const { deckKey, onDeviceInitialized, exhausted, reset } = useWebglContextRecovery();
  const [hoveredDma, setHoveredDma] = React.useState<string | null>(null);

  const selectedSet = React.useMemo(() => new Set(selectedDMAs), [selectedDMAs]);
  const focusSet = React.useMemo(
    () => new Set(focusDmaCodes ?? []),
    [focusDmaCodes],
  );

  // null/undefined here ≡ "no restriction" (every market selectable).
  const allowedSet = React.useMemo<Set<string> | null>(
    () => (allowedDmaCodes && allowedDmaCodes.length > 0 ? new Set(allowedDmaCodes) : null),
    [allowedDmaCodes],
  );

  const toggleDma = React.useCallback(
    (code: string) => {
      if (allowedSet && !allowedSet.has(code)) return;
      if (selectedSet.has(code)) {
        onChange(selectedDMAs.filter((d) => d !== code));
      } else {
        onChange([...selectedDMAs, code]);
      }
    },
    [allowedSet, selectedSet, selectedDMAs, onChange],
  );

  // Track the map's actual rendered width so the fit is correct at every
  // screen size (deck.gl doesn't auto-refit to its container). Measured
  // before paint via useLayoutEffect, then kept current with a
  // ResizeObserver. 0 until measured; the view-state memo falls back to a
  // nominal width for that first pass.
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = React.useState(0);
  React.useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setContainerWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setContainerWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const mapWidth = containerWidth || 800;

  // The static view the map shows (controller interactions are disabled,
  // so this IS the map). Fits the selectable region (allowedDmaCodes when
  // declared, else the coverage itself) at the measured container size,
  // refit on resize.
  const viewState = React.useMemo(() => {
    const fitKeys = allowedDmaCodes && allowedDmaCodes.length > 0 ? allowedDmaCodes : focusDmaCodes;
    // Exact fit, no bbox margin (tuned to zero in CTV-705 review): the
    // aspect-ratio slack on the wide axis plus the fixed pixel padding is
    // all the breathing room the picker needs.
    return computeFitViewState(dmaGeoJson, fitKeys, dmaKeyOf, mapWidth, MAP_HEIGHT, {
      maxZoom: DMA_MAX_ZOOM,
    });
  }, [dmaGeoJson, focusDmaCodes, allowedDmaCodes, mapWidth]);

  // Persistent labels for coverage + selected markets, anchored inside
  // their largest polygon. Everything else identifies via the hover tooltip.
  const labels = React.useMemo(() => {
    if (!dmaGeoJson) return [];
    const wanted = new Set<string>([...focusSet, ...selectedSet]);
    if (wanted.size === 0) return [];
    const out: { position: [number, number]; text: string; code: string }[] = [];
    for (const feat of dmaGeoJson.features) {
      const code = dmaKeyOf(feat);
      if (!code || !wanted.has(code) || !feat.geometry) continue;
      const pos = featureLabelPoint(feat.geometry);
      if (!pos) continue;
      out.push({ position: pos, text: dmaNameForCode(code), code });
    }
    return out;
  }, [dmaGeoJson, focusSet, selectedSet]);

  // Parse the accent color (hex, "#RGB" or "#RRGGBB") into the RGBA tuple
  // deck.gl wants. Defaults silently to a neutral blue if the prop isn't a
  // recognizable hex.
  const accentRgb = React.useMemo<[number, number, number]>(() => {
    const hex = accentColor.replace('#', '');
    const expand = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
    if (!/^[0-9a-f]{6}$/i.test(expand)) return [37, 99, 235];
    return [
      parseInt(expand.slice(0, 2), 16),
      parseInt(expand.slice(2, 4), 16),
      parseInt(expand.slice(4, 6), 16),
    ];
  }, [accentColor]);

  const layers = React.useMemo(() => {
    const result: Layer[] = [];

    if (dmaGeoJson) {
      result.push(
        new GeoJsonLayer({
          id: 'dmas-interactive',
          data: dmaGeoJson,
          filled: true,
          stroked: true,
          pickable: true,
          getFillColor: (f: GeoJSON.Feature) => {
            const code = dmaKeyOf(f) ?? '';
            if (selectedSet.has(code)) return [...accentRgb, 255];
            // Disallowed markets render as a flat very-light gray with no
            // hover affordance, visually present but clearly inert.
            if (allowedSet && !allowedSet.has(code)) return [241, 245, 249, 255];
            if (hoveredDma === code) return [...accentRgb, 110];
            return [224, 231, 239, 255];
          },
          getLineColor: [255, 255, 255, 255],
          getLineWidth: 1,
          lineWidthUnits: 'pixels' as const,
          onClick: (info: { object?: GeoJSON.Feature }) => {
            if (!info.object) return;
            const code = dmaKeyOf(info.object);
            if (code) toggleDma(code);
          },
          onHover: (info: { object?: GeoJSON.Feature }) => {
            const code = info.object ? dmaKeyOf(info.object) : null;
            // Skip hover highlight on disallowed markets so the user
            // doesn't get false-positive interactivity cues.
            if (allowedSet && code && !allowedSet.has(code)) {
              setHoveredDma(null);
            } else {
              setHoveredDma(code);
            }
          },
          updateTriggers: {
            getFillColor: [selectedDMAs, hoveredDma, accentRgb, allowedDmaCodes],
          },
        }),
      );
    }

    if (labels.length > 0) {
      result.push(
        new TextLayer({
          id: 'dma-labels',
          data: labels,
          getPosition: (d: { position: number[] }) => d.position as [number, number],
          getText: (d: { text: string }) => d.text,
          getSize: 10,
          getColor: (d: { code: string }) =>
            selectedSet.has(d.code) ? [255, 255, 255, 255] : [100, 116, 139, 255],
          fontWeight: 600,
          getTextAnchor: 'middle',
          getAlignmentBaseline: 'center',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          sizeUnits: 'pixels' as const,
          billboard: false,
          updateTriggers: {
            getColor: [selectedDMAs],
          },
        }),
      );
    }

    return result;
  }, [dmaGeoJson, labels, selectedSet, hoveredDma, selectedDMAs, toggleDma, accentRgb, allowedSet, allowedDmaCodes]);

  return (
    <div
      ref={containerRef}
      className="rounded-lg border border-slate-200 overflow-hidden relative bg-white"
      style={{ height: MAP_HEIGHT }}
      // deck.gl's onHover doesn't always fire with no info when the cursor
      // exits the canvas across an edge (no intermediate pointer event in
      // empty whitespace), so the hover highlight stays "stuck" on the
      // last market. Clearing on the outer div's mouseleave covers every
      // exit direction.
      onMouseLeave={() => setHoveredDma(null)}
    >
      {/* Mount the WebGL canvas only once data exists (CTV-689): the overlay
          states need no context, and a blank Deck would burn one against the
          browser's per-page cap while loading/erroring. */}
      {exhausted ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-slate-50 text-[12px] text-slate-500">
          <span>Map rendering was interrupted by the browser.</span>
          <button
            type="button"
            onClick={reset}
            className="rounded-md border border-slate-300 bg-white px-3 py-1 text-[12px] font-medium text-slate-700 hover:bg-slate-100"
          >
            Reload map
          </button>
        </div>
      ) : dmaGeoJson ? (
        <DeckGL
          key={deckKey}
          onDeviceInitialized={onDeviceInitialized}
          viewState={viewState}
          controller={{
            scrollZoom: false,
            dragPan: false,
            dragRotate: false,
            doubleClickZoom: false,
            touchZoom: false,
            touchRotate: false,
            keyboard: false,
          }}
          layers={layers}
          getTooltip={({ object }: { object?: GeoJSON.Feature }) => {
            if (!object) return null;
            const code = dmaKeyOf(object);
            if (!code) return null;
            // No tooltip on disallowed markets; naming them would suggest
            // they're selectable.
            if (allowedSet && !allowedSet.has(code)) return null;
            return `${dmaNameForCode(code)} (${code})`;
          }}
          style={{ position: 'absolute', top: '0', left: '0', width: '100%', height: '100%', background: '#ffffff' }}
          getCursor={({ isHovering }: { isHovering: boolean }) => (isHovering ? 'pointer' : 'default')}
        />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-slate-50 text-[12px] text-slate-500">
          {boundariesStatus === 'error' ? (
            <>
              <span>Couldn&apos;t load metro boundaries.</span>
              <button
                type="button"
                onClick={retryBoundaries}
                className="rounded-md border border-slate-300 bg-white px-3 py-1 text-[12px] font-medium text-slate-700 hover:bg-slate-100"
              >
                Try again
              </button>
            </>
          ) : (
            <span className="inline-flex items-center">
              <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
              {boundariesStatus === 'retrying'
                ? 'Map data failed to load. Retrying…'
                : 'Loading metros…'}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default InteractiveDmasMap;
